// Authorities: name, kind, numbers, explicit site assignments, and a GeoJSON jurisdiction
// (Polygon / MultiPolygon, lon/lat) validated in the browser before saving.

import { useState } from 'react';
import { api, AUTHORITY_KINDS, type Authority, type AuthorityKind, type Jurisdiction } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { Empty, ErrorBox, Loading, Pill } from '../../components/ui.tsx';
import { human } from '../../console/util.ts';
import { useLoad } from '../../hooks.ts';
import { href, navigate } from '../../router.ts';
import { SaveBar, Toggle } from './common.tsx';

type Parsed = { ok: true; value: Jurisdiction | null; summary: string } | { ok: false; error: string };

function isPos(p: unknown): p is number[] {
  return Array.isArray(p) && p.length >= 2 && p.every((n) => typeof n === 'number' && Number.isFinite(n));
}

function checkPolygon(rings: unknown, where: string): string | null {
  if (!Array.isArray(rings) || rings.length === 0) return `${where}: a polygon is a list of rings`;
  for (let r = 0; r < rings.length; r++) {
    const ring: unknown = rings[r];
    if (!Array.isArray(ring) || ring.length < 4) return `${where} ring ${r + 1}: needs at least 4 positions (first = last)`;
    for (let i = 0; i < ring.length; i++) {
      const p: unknown = ring[i];
      if (!isPos(p)) return `${where} ring ${r + 1} position ${i + 1}: expected [lon, lat]`;
      const [lon, lat] = p;
      if (lon === undefined || lat === undefined || lon < -180 || lon > 180 || lat < -90 || lat > 90) return `${where} ring ${r + 1} position ${i + 1}: [${p.join(', ')}] is outside lon −180…180 / lat −90…90 (order is [lon, lat])`;
    }
    const a: unknown = ring[0];
    const b: unknown = ring[ring.length - 1];
    if (!isPos(a) || !isPos(b) || a[0] !== b[0] || a[1] !== b[1]) return `${where} ring ${r + 1}: not closed (the last position must repeat the first)`;
  }
  return null;
}

/** Accepts a Polygon / MultiPolygon geometry, a Feature, or a FeatureCollection with one feature. */
export function parseJurisdiction(text: string): Parsed {
  const t = text.trim();
  if (!t) return { ok: true, value: null, summary: 'No jurisdiction: matched only by explicit site assignments.' };
  let raw: unknown;
  try {
    raw = JSON.parse(t);
  } catch (e) {
    return { ok: false, error: `Not valid JSON: ${e instanceof Error ? e.message : String(e)}` };
  }
  const rec = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
  if (rec(raw) && raw.type === 'FeatureCollection') {
    const f = raw.features;
    if (!Array.isArray(f) || f.length !== 1) return { ok: false, error: 'A FeatureCollection must hold exactly one feature' };
    raw = f[0];
  }
  if (rec(raw) && raw.type === 'Feature') raw = raw.geometry;
  if (!rec(raw)) return { ok: false, error: 'Expected a GeoJSON object' };
  if (raw.type === 'Polygon') {
    const err = checkPolygon(raw.coordinates, 'Polygon');
    if (err) return { ok: false, error: err };
    const c = raw.coordinates as number[][][];
    return { ok: true, value: { type: 'Polygon', coordinates: c }, summary: `Polygon · ${c.length} ring(s) · ${c[0]?.length ?? 0} positions` };
  }
  if (raw.type === 'MultiPolygon') {
    const polys = raw.coordinates;
    if (!Array.isArray(polys) || polys.length === 0) return { ok: false, error: 'MultiPolygon: coordinates must be a list of polygons' };
    for (let i = 0; i < polys.length; i++) {
      const err = checkPolygon(polys[i], `Polygon ${i + 1}`);
      if (err) return { ok: false, error: err };
    }
    return { ok: true, value: { type: 'MultiPolygon', coordinates: polys as number[][][][] }, summary: `MultiPolygon · ${polys.length} polygons` };
  }
  return { ok: false, error: `Geometry type must be Polygon or MultiPolygon (got ${String(raw.type)})` };
}

type Draft = Omit<Authority, 'id' | 'jurisdiction'>;

function Editor({ a, canEdit, onSaved }: { a: Authority | null; canEdit: boolean; onSaved: (a: Authority) => void }) {
  const sites = useLoad((signal) => api.sites(undefined, signal), []);
  const init: Draft = a
    ? { name: a.name, kind: a.kind, emergency: a.emergency, nonEmergency: a.nonEmergency, note: a.note, siteIds: a.siteIds, active: a.active }
    : { name: '', kind: 'police', emergency: '911', nonEmergency: '', note: null, siteIds: [], active: true };
  const initGeo = a?.jurisdiction ? JSON.stringify(a.jurisdiction, null, 2) : '';
  const [d, setD] = useState<Draft>(init);
  const [geo, setGeo] = useState(initGeo);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const parsed = parseJurisdiction(geo);
  const dirty = JSON.stringify(d) !== JSON.stringify(init) || geo.trim() !== initGeo.trim() || !a;
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => {
    setSaved(false);
    setD((o) => ({ ...o, [k]: v }));
  };
  const needle = q.trim().toLowerCase();
  const siteList = (sites.data ?? []).filter((s) => s.active && (!needle || `${s.name} ${s.companyName}`.toLowerCase().includes(needle))).sort((x, y) => x.name.localeCompare(y.name));

  const save = async () => {
    if (!parsed.ok) return setErr(new Error(`Jurisdiction: ${parsed.error}`));
    if (!d.name.trim()) return setErr(new Error('Give the authority a name.'));
    setBusy(true);
    setErr(null);
    try {
      const body = { ...d, name: d.name.trim(), emergency: d.emergency?.trim() || null, nonEmergency: d.nonEmergency?.trim() || null, note: d.note?.trim() || null, jurisdiction: parsed.value };
      const r = a ? await api.patchAuthority(a.id, body) : await api.createAuthority(body);
      setSaved(true);
      onSaved(r);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <fieldset disabled={!canEdit} className="tabbody">
      <div className="page-h">
        <div>
          <h2>{a ? a.name : 'New authority'}</h2>
          <p>Operators see the matched authority on the Contacts tab and in the Dispatch drawer. Explicit site assignments win over the jurisdiction polygon.</p>
        </div>
      </div>
      <div className="fgrid">
        <label className="field">
          <span className="lbl">Name</span>
          <input value={d.name} onChange={(e) => set('name', e.target.value)} />
        </label>
        <label className="field">
          <span className="lbl">Kind</span>
          <select value={d.kind} onChange={(e) => set('kind', e.target.value as AuthorityKind)}>
            {AUTHORITY_KINDS.map((k) => (
              <option key={k} value={k}>
                {human(k)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="lbl">Non-emergency number</span>
          <input value={d.nonEmergency ?? ''} onChange={(e) => set('nonEmergency', e.target.value)} className="mono" placeholder="(480) 555-0142" />
        </label>
        <label className="field">
          <span className="lbl">Emergency (life safety only)</span>
          <input value={d.emergency ?? ''} onChange={(e) => set('emergency', e.target.value)} className="mono" placeholder="911" />
        </label>
      </div>
      <label className="field">
        <span className="lbl">Note for operators</span>
        <input value={d.note ?? ''} onChange={(e) => set('note', e.target.value)} />
      </label>
      <Toggle checked={d.active} onChange={(v) => set('active', v)} label="Active" disabled={!canEdit} />

      <div className="cols2">
        <div className="sec">
          <div className="lbl">
            <span>Jurisdiction · GeoJSON (lon, lat)</span>
            {parsed.ok ? <Pill tone="ok">valid</Pill> : <Pill tone="bad">invalid</Pill>}
          </div>
          <textarea
            className="mono geo"
            rows={12}
            value={geo}
            onChange={(e) => (setSaved(false), setGeo(e.target.value))}
            spellCheck={false}
            aria-invalid={!parsed.ok}
            aria-describedby="geo-msg"
            placeholder='{ "type": "Polygon", "coordinates": [[[-111.9, 33.3], [-111.6, 33.3], [-111.6, 33.5], [-111.9, 33.5], [-111.9, 33.3]]] }'
          />
          <span id="geo-msg" className={parsed.ok ? 'note' : 'inline-err'}>
            {parsed.ok ? parsed.summary : parsed.error}
          </span>
          {parsed.ok && parsed.value && canEdit && (
            <button type="button" className="btn sm self-start" onClick={() => setGeo(JSON.stringify(parsed.value, null, 2))}>
              Format
            </button>
          )}
        </div>
        <div className="sec">
          <div className="lbl">
            <span>Assigned sites · {d.siteIds.length}</span>
          </div>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sites" aria-label="Search sites" />
          <div className="checklist">
            {sites.error && !sites.data ? <ErrorBox error={sites.error} onRetry={sites.reload} title="Could not load sites" /> : null}
            {!sites.data && !sites.error ? <Loading label="Loading sites…" /> : null}
            {siteList.map((s) => (
              <label key={s.id} className="ck-inline">
                <input type="checkbox" checked={d.siteIds.includes(s.id)} onChange={(e) => set('siteIds', e.target.checked ? [...d.siteIds, s.id] : d.siteIds.filter((x) => x !== s.id))} />
                <span>
                  {s.name} <span className="note">{s.companyName}</span>
                </span>
              </label>
            ))}
            {d.siteIds
              .filter((id) => !(sites.data ?? []).some((s) => s.id === id))
              .map((id) => (
                <label key={id} className="ck-inline">
                  <input type="checkbox" checked onChange={() => set('siteIds', d.siteIds.filter((x) => x !== id))} />
                  <span className="mono">{id}</span>
                </label>
              ))}
          </div>
        </div>
      </div>
      <SaveBar
        canEdit={canEdit}
        dirty={dirty}
        busy={busy}
        error={err}
        saved={saved}
        onSave={() => void save()}
        onReset={() => (setD(init), setGeo(initGeo))}
        label={a ? 'Save' : 'Create authority'}
      />
    </fieldset>
  );
}

export function AuthoritiesEditor({ canEdit, selectedId }: { canEdit: boolean; selectedId: string | null }) {
  const load = useLoad((signal) => api.authorities(signal), []);
  const list = (load.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
  const isNew = selectedId === 'new';
  const sel = isNew ? null : (list.find((a) => a.id === selectedId) ?? null);
  return (
    <div className="split2">
      <div className="card2 flush">
        <div className="card-h pad">
          <h3>
            <Icon name="shield" />
            Authorities
          </h3>
          {canEdit && (
            <div className="card-r">
              <a className="btn sm" href={href.setup('authorities', 'new')}>
                <Icon name="plus" />
                New
              </a>
            </div>
          )}
        </div>
        {load.error && !load.data ? (
          <ErrorBox error={load.error} onRetry={load.reload} title="Could not load authorities" />
        ) : !load.data ? (
          <Loading label="Loading authorities…" />
        ) : list.length === 0 ? (
          <Empty>No authorities yet.</Empty>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th scope="col">Agency</th>
                  <th scope="col">Non-emergency</th>
                  <th scope="col">Jurisdiction</th>
                  <th scope="col" className="num">Sites</th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a.id} className={`click${a.id === selectedId ? ' sel' : ''}${a.active ? '' : ' inactive'}`} onClick={() => navigate(href.setup('authorities', a.id))} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate(href.setup('authorities', a.id))}>
                    <td>
                      <a href={href.setup('authorities', a.id)} onClick={(e) => e.stopPropagation()}>
                        {a.name}
                      </a>
                      <div className="note">{human(a.kind)}</div>
                    </td>
                    <td className="mono">{a.nonEmergency ?? '—'}</td>
                    <td className="small">{a.jurisdiction ? a.jurisdiction.type : <span className="dim">none</span>}</td>
                    <td className="mono num">{a.siteIds.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="card2">
        {sel || isNew ? (
          <Editor
            key={sel ? sel.id : 'new'}
            a={sel}
            canEdit={canEdit}
            onSaved={(r) => {
              load.setData((l) => [...(l ?? []).filter((x) => x.id !== r.id), r]);
              if (!sel) navigate(href.setup('authorities', r.id), { replace: true });
            }}
          />
        ) : (
          <div className="state empty">{selectedId && load.data ? 'That authority no longer exists.' : 'Pick an authority to view or edit it.'}</div>
        )}
      </div>
    </div>
  );
}
